<template>
  <div v-if="visible" class="modal-backdrop competition-stats-backdrop" @click.self="$emit('close')">
    <div class="modal-content competition-stats-modal">
      <div class="competition-stats-header">
        <div class="competition-stats-heading">
          <p class="competition-stats-eyebrow">{{ tOr('menu.statistics', 'Statistics') }}</p>
          <h3>{{ modalTitle }}</h3>
          <p class="competition-stats-subtitle">{{ modalSubtitle }}</p>
        </div>
        <button type="button" class="competition-stats-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="competition-stats-body">
        <div v-if="loading" class="competition-stats-state">
          {{ tOr('profile.stats.loading', 'Loading statistics...') }}
        </div>

        <div v-else-if="error" class="competition-stats-state competition-stats-state-error">
          {{ error }}
        </div>

        <div v-else-if="!stats" class="competition-stats-state">
          {{ tOr('judging_program.statistics.empty', 'No statistics available yet.') }}
        </div>

        <template v-else>
          <section class="competition-selector-section">
            <div class="competition-section-header">
              <h4>{{ tOr('judging_program.statistics.round_drilldown', 'Round drill-down') }}</h4>
            </div>
            <div class="competition-selector-grid">
              <label class="competition-selector-field">
                <span>{{ tOr('judging_program.statistics.stage_selector', 'Stage') }}</span>
                <select v-model="selectedStageCode" class="form-select competition-selector-input">
                  <option value="">{{ tOr('judging_program.statistics.select_stage', 'Select a stage') }}</option>
                  <option v-for="stage in selectableStages" :key="stage.stage_code || stage.label" :value="stage.stage_code">
                    {{ stage.label }}
                  </option>
                </select>
              </label>

              <label class="competition-selector-field">
                <span>{{ tOr('judging_program.statistics.round_selector', 'Round') }}</span>
                <select v-model="selectedRoundId" class="form-select competition-selector-input" :disabled="!selectedStageCode || !roundOptions.length">
                  <option value="">{{ roundPlaceholder }}</option>
                  <option v-for="round in roundOptions" :key="round.round_id" :value="String(round.round_id)">
                    {{ round.label }}
                  </option>
                </select>
              </label>
            </div>
          </section>

          <section v-if="hasSelectedRound" class="competition-round-panel">
            <div class="competition-section-header">
              <h4>{{ selectedRoundHeading }}</h4>
              <span v-if="selectedStage" class="competition-stage-tag">{{ selectedStage.label }}</span>
            </div>
            <RoundStatisticsContent
              :key="selectedRoundContentKey"
              :loading="roundStatisticsLoading"
              :error="roundStatisticsError"
              :stats="roundStatistics"
              :emptyMessage="roundEmptyMessage"
            />
          </section>

          <template v-else>
            <section class="competition-stats-summary">
              <article v-for="card in summaryCards" :key="card.key" class="competition-summary-card">
                <span class="competition-summary-label">{{ card.label }}</span>
                <strong class="competition-summary-value">{{ card.value }}</strong>
              </article>
            </section>

            <section class="competition-stats-charts">
              <article v-if="roundsByStagePoints.length" class="competition-chart-card">
                <div class="competition-section-header">
                  <h4>{{ tOr('judging_program.statistics.stages_overview', 'Rounds by stage') }}</h4>
                </div>
                <div class="competition-chart-box competition-chart-box-bars">
                  <canvas ref="roundsByStageChart"></canvas>
                </div>
              </article>

              <article v-if="averageScorePoints.length" class="competition-chart-card">
                <div class="competition-section-header">
                  <h4>{{ tOr('judging_program.statistics.average_score', 'Average score by stage') }}</h4>
                </div>
                <div class="competition-chart-box competition-chart-box-line">
                  <canvas ref="averageScoreChart"></canvas>
                </div>
              </article>

              <article v-if="repeatedTricksPoints.length" class="competition-chart-card competition-chart-card-secondary">
                <div class="competition-section-header">
                  <h4>{{ tOr('judging_program.statistics.repeated_tricks', 'Trick repetition') }}</h4>
                </div>
                <div class="competition-chart-box competition-chart-box-short competition-chart-box-horizontal">
                  <canvas ref="topTricksChart"></canvas>
                </div>
              </article>

              <article v-if="bestTricksPoints.length" class="competition-chart-card competition-chart-card-secondary">
                <div class="competition-section-header">
                  <h4>{{ tOr('judging_program.statistics.best_tricks', 'Best tricks') }}</h4>
                </div>
                <div class="competition-chart-box competition-chart-box-short competition-chart-box-horizontal">
                  <canvas ref="bestTricksChart"></canvas>
                </div>
              </article>

              <article v-if="tricksByLevelPoints.length" class="competition-chart-card competition-chart-card-secondary">
                <div class="competition-section-header">
                  <h4>{{ tOr('profile.stats.tricks_by_level', 'Tricks by level') }}</h4>
                </div>
                <div class="competition-chart-box competition-chart-box-doughnut">
                  <canvas ref="tricksByLevelChart"></canvas>
                </div>
              </article>

              <article v-if="trickCategoryPoints.length" class="competition-chart-card competition-chart-card-secondary">
                <div class="competition-section-header">
                  <h4>{{ tOr('profile.stats.trick_categories', 'Trick categories') }}</h4>
                </div>
                <div class="competition-chart-box competition-chart-box-doughnut">
                  <canvas ref="trickCategoriesChart"></canvas>
                </div>
              </article>
            </section>

            <section class="competition-stage-section">
              <div class="competition-section-header">
                <h4>{{ tOr('judging_program.statistics.stages', 'Stages') }}</h4>
                <span class="competition-count-pill">{{ stages.length }}</span>
              </div>
              <div class="competition-stage-grid">
                <button
                  v-for="stage in stages"
                  :key="stage.stage_code || stage.label"
                  type="button"
                  :class="['competition-stage-card', { 'is-selected': stage.stage_code === selectedStageCode }]"
                  @click="selectStage(stage.stage_code)"
                >
                  <div class="competition-stage-card-head">
                    <strong>{{ stage.label }}</strong>
                    <span class="competition-stage-pill">{{ stage.rounds_count }}</span>
                  </div>
                  <div class="competition-stage-card-meta">
                    <span>{{ tOr('profile.stats.tricks', 'Tricks') }}: {{ formatInteger(stage.validated_tricks_count) }}</span>
                    <span>{{ tOr('profile.stats.unique_tricks', 'Unique tricks') }}: {{ formatInteger(stage.unique_tricks_count) }}</span>
                    <span>{{ tOr('judging_program.statistics.average_score', 'Avg. score') }}: {{ formatStatValue(stage.average_score) }}</span>
                    <span>{{ tOr('profile.stats.combos', 'Combos') }}: {{ formatInteger(stage.combos_count) }}</span>
                  </div>
                </button>
              </div>
            </section>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { mapState } from 'vuex';
import RoundStatisticsContent from '@/components/competitions/RoundStatisticsContent.vue';

export default {
  name: 'CompetitionStatisticsModal',
  components: { RoundStatisticsContent },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    stats: {
      type: Object,
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      roundsByStageChartInstance: null,
      averageScoreChartInstance: null,
      topTricksChartInstance: null,
      bestTricksChartInstance: null,
      tricksByLevelChartInstance: null,
      trickCategoriesChartInstance: null,
      chartRenderToken: 0,
      roundRequestToken: 0,
      selectedStageCode: '',
      selectedRoundId: '',
      roundStatistics: null,
      roundStatisticsLoading: false,
      roundStatisticsError: '',
    };
  },
  computed: {
    ...mapState('user', ['token']),
    competitionContext() {
      return this.stats?.competition_context || {};
    },
    modalTitle() {
      return this.competitionContext.competition_name || this.tOr('judging_program.statistics.competition_summary', 'Competition statistics');
    },
    modalSubtitle() {
      return [this.competitionContext.rule_name, this.competitionContext.country_name].filter(Boolean).join(' · ');
    },
    summaryCards() {
      const summary = this.stats?.summary || {};
      return [
        { key: 'stages', label: this.tOr('judging_program.statistics.stages', 'Stages'), value: this.formatInteger(summary.stages_count) },
        { key: 'rounds', label: this.tOr('judging_program.statistics.rounds', 'Rounds'), value: this.formatInteger(summary.rounds_count) },
        { key: 'athletes', label: this.tOr('competitions.view.athletes', 'Athletes'), value: this.formatInteger(summary.athletes_count) },
        { key: 'avg', label: this.tOr('judging_program.statistics.average_score', 'Average score'), value: this.formatStatValue(summary.average_score) },
        { key: 'best', label: this.tOr('judging_program.statistics.best_score', 'Best score'), value: this.formatStatValue(summary.best_score) },
        { key: 'validated', label: this.tOr('profile.stats.validated_tricks', 'Validated tricks'), value: this.formatInteger(summary.validated_tricks_count) },
        { key: 'unique', label: this.tOr('profile.stats.unique_tricks', 'Unique tricks'), value: this.formatInteger(summary.unique_tricks_count) },
        { key: 'combos', label: this.tOr('profile.stats.combos', 'Combos'), value: this.formatInteger(summary.combos_count) },
      ];
    },
    stages() {
      return Array.isArray(this.stats?.stages) ? this.stats.stages : [];
    },
    selectableStages() {
      const stagesWithRounds = this.stages.filter((stage) => stage.rounds_count > 0);
      return stagesWithRounds.length ? stagesWithRounds : this.stages;
    },
    selectedStage() {
      return this.stages.find((stage) => stage.stage_code === this.selectedStageCode) || null;
    },
    roundOptions() {
      const rounds = Array.isArray(this.selectedStage?.rounds) ? this.selectedStage.rounds : [];
      const eligible = rounds.filter((round) => round.statistics_available);
      return eligible.length ? eligible : rounds;
    },
    roundPlaceholder() {
      if (!this.selectedStageCode) {
        return this.tOr('judging_program.statistics.select_stage_first', 'Select a stage first');
      }

      return this.roundOptions.length
        ? this.tOr('judging_program.statistics.select_round', 'Select a round')
        : this.tOr('judging_program.statistics.no_rounds', 'No rounds available');
    },
    selectedRoundOption() {
      return this.roundOptions.find((round) => String(round.round_id) === String(this.selectedRoundId)) || null;
    },
    selectedRoundHeading() {
      return this.roundStatistics?.round_context?.athlete_name
        ? `${this.roundStatistics.round_context.athlete_name} · ${this.roundStatistics.round_context.stage_label || this.roundStatistics.round_context.round_name || ''}`.replace(/ · $/, '')
        : (this.selectedRoundOption?.label || this.tOr('judging_program.statistics.round_summary', 'Round statistics'));
    },
    roundEmptyMessage() {
      return this.tOr('judging_program.statistics.select_round_prompt', 'Select a round to load the detailed statistics.');
    },
    roundsByStagePoints() {
      return Array.isArray(this.stats?.charts?.rounds_by_stage) ? this.stats.charts.rounds_by_stage : [];
    },
    averageScorePoints() {
      return Array.isArray(this.stats?.charts?.average_score_by_stage) ? this.stats.charts.average_score_by_stage : [];
    },
    repeatedTricksPoints() {
      if (Array.isArray(this.stats?.charts?.repeated_tricks)) {
        return this.stats.charts.repeated_tricks;
      }

      return Array.isArray(this.stats?.charts?.top_tricks) ? this.stats.charts.top_tricks : [];
    },
    bestTricksPoints() {
      return Array.isArray(this.stats?.charts?.best_tricks) ? this.stats.charts.best_tricks : [];
    },
    tricksByLevelPoints() {
      const points = Array.isArray(this.stats?.charts?.tricks_by_level) ? this.stats.charts.tricks_by_level : [];
      const pointMap = new Map(points.map((point) => [Number(point.level_id), point]));

      return [1, 2, 3, 4].map((levelId) => {
        const point = pointMap.get(levelId);

        return {
          level_id: levelId,
          label: point?.label || `Level ${levelId}`,
          value: Number(point?.validated_count ?? point?.value ?? 0) || 0,
        };
      });
    },
    trickCategoryPoints() {
      return Array.isArray(this.stats?.charts?.tricks_by_category) ? this.stats.charts.tricks_by_category : [];
    },
    hasSelectedRound() {
      return Boolean(this.selectedRoundId);
    },
    selectedRoundContentKey() {
      return [
        this.selectedRoundId || 'general',
        this.roundStatistics?.round_context?.round_id || 'empty',
      ].join(':');
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.resetRoundState();
        this.scheduleRenderCharts();
        return;
      }

      this.chartRenderToken += 1;
      this.roundRequestToken += 1;
      this.destroyCharts();
    },
    stats() {
      this.resetRoundState();
      this.scheduleRenderCharts();
    },
    loading() {
      this.scheduleRenderCharts();
    },
    error() {
      this.scheduleRenderCharts();
    },
    selectedStageCode() {
      this.selectedRoundId = '';
      this.roundStatistics = null;
      this.roundStatisticsError = '';
      this.roundStatisticsLoading = false;
      this.scheduleRenderCharts();
    },
    selectedRoundId(value, oldValue) {
      if (!value) {
        this.roundStatistics = null;
        this.roundStatisticsError = '';
        this.roundStatisticsLoading = false;
        this.scheduleRenderCharts();
        return;
      }

      if (!this.visible || value === oldValue) {
        return;
      }

      this.fetchRoundStatistics(Number(value));
    },
  },
  beforeUnmount() {
    this.chartRenderToken += 1;
    this.roundRequestToken += 1;
    this.destroyCharts();
  },
  methods: {
    tOr(key, fallback) {
      if (typeof this.$te === 'function' && this.$te(key)) {
        return this.$t(key);
      }

      return fallback;
    },
    formatStatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return value;
      }

      return numericValue.toFixed(2);
    },
    formatInteger(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return '0';
      }

      return String(Math.round(numericValue));
    },
    selectStage(stageCode) {
      this.selectedStageCode = stageCode || '';
    },
    resetRoundState() {
      this.roundRequestToken += 1;
      this.selectedStageCode = '';
      this.selectedRoundId = '';
      this.roundStatistics = null;
      this.roundStatisticsLoading = false;
      this.roundStatisticsError = '';
    },
    async fetchRoundStatistics(roundId) {
      if (!roundId || Number(this.roundStatistics?.round_context?.round_id) === Number(roundId)) {
        return;
      }

      const requestToken = ++this.roundRequestToken;
      this.roundStatisticsLoading = true;
      this.roundStatisticsError = '';
      this.roundStatistics = null;

      try {
        const response = await this.$axios.get(`/rounds/${roundId}/stats`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        if (requestToken !== this.roundRequestToken) {
          return;
        }

        this.roundStatistics = response?.data?.stats || response?.data?.statistics || null;
      } catch (requestError) {
        if (requestToken !== this.roundRequestToken) {
          return;
        }

        this.roundStatistics = null;
        this.roundStatisticsError = requestError?.response?.data?.message || 'Unable to load round statistics.';
      } finally {
        if (requestToken === this.roundRequestToken) {
          this.roundStatisticsLoading = false;
        }
      }
    },
    scheduleRenderCharts() {
      const token = ++this.chartRenderToken;

      this.$nextTick(() => {
        if (token !== this.chartRenderToken || !this.visible) {
          return;
        }

        this.renderCharts();
      });
    },
    destroyCharts() {
      [
        'roundsByStageChartInstance',
        'averageScoreChartInstance',
        'topTricksChartInstance',
        'bestTricksChartInstance',
        'tricksByLevelChartInstance',
        'trickCategoriesChartInstance',
      ].forEach((key) => {
        if (this[key]) {
          this[key].stop();
          this[key].destroy();
          this[key] = null;
        }
      });
    },
    renderCharts() {
      this.destroyCharts();

      if (this.loading || this.error || !this.stats) {
        return;
      }

      this.renderRoundsByStageChart();
      this.renderAverageScoreChart();
      this.renderRepeatedTricksChart();
      this.renderBestTricksChart();
      this.renderTricksByLevelChart();
      this.renderTrickCategoriesChart();
    },
    renderRoundsByStageChart() {
      const canvas = this.$refs.roundsByStageChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.roundsByStagePoints.length) {
        return;
      }

      this.roundsByStageChartInstance = new Chart(context, {
        type: 'bar',
        data: {
          labels: this.roundsByStagePoints.map((item) => item.label),
          datasets: [{
            data: this.roundsByStagePoints.map((item) => Number(item.value) || 0),
            backgroundColor: '#0f766e',
            borderRadius: 10,
          }],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      });
    },
    renderAverageScoreChart() {
      const canvas = this.$refs.averageScoreChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.averageScorePoints.length) {
        return;
      }

      this.averageScoreChartInstance = new Chart(context, {
        type: 'line',
        data: {
          labels: this.averageScorePoints.map((item) => item.label),
          datasets: [{
            data: this.averageScorePoints.map((item) => Number(item.value) || 0),
            borderColor: '#1d4ed8',
            backgroundColor: 'rgba(29, 78, 216, 0.16)',
            tension: 0.25,
            fill: true,
          }],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true },
          },
        },
      });
    },
    renderRepeatedTricksChart() {
      const canvas = this.$refs.topTricksChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.repeatedTricksPoints.length) {
        return;
      }

      this.topTricksChartInstance = new Chart(context, {
        type: 'bar',
        data: {
          labels: this.repeatedTricksPoints.map((item) => item.label),
          datasets: [{
            data: this.repeatedTricksPoints.map((item) => Number(item.value) || 0),
            backgroundColor: '#111827',
            borderRadius: 8,
          }],
        },
        options: {
          animation: false,
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { beginAtZero: true, ticks: { precision: 0 } },
            y: { ticks: { autoSkip: false } },
          },
        },
      });
    },
    renderBestTricksChart() {
      const canvas = this.$refs.bestTricksChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.bestTricksPoints.length) {
        return;
      }

      this.bestTricksChartInstance = new Chart(context, {
        type: 'bar',
        data: {
          labels: this.bestTricksPoints.map((item) => item.label),
          datasets: [{
            data: this.bestTricksPoints.map((item) => Number(item.value) || 0),
            backgroundColor: '#0f766e',
            borderRadius: 8,
          }],
        },
        options: {
          animation: false,
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { beginAtZero: true },
            y: { ticks: { autoSkip: false } },
          },
        },
      });
    },
    renderTricksByLevelChart() {
      const canvas = this.$refs.tricksByLevelChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.tricksByLevelPoints.length) {
        return;
      }

      this.tricksByLevelChartInstance = new Chart(context, {
        type: 'doughnut',
        data: {
          labels: this.tricksByLevelPoints.map((item) => item.label),
          datasets: [{
            data: this.tricksByLevelPoints.map((item) => Number(item.value) || 0),
            backgroundColor: ['#94a3b8', '#60a5fa', '#34d399', '#f59e0b'],
            borderRadius: 8,
            borderWidth: 0,
          }],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' } },
          cutout: '58%',
        },
      });
    },
    renderTrickCategoriesChart() {
      const canvas = this.$refs.trickCategoriesChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.trickCategoryPoints.length) {
        return;
      }

      this.trickCategoriesChartInstance = new Chart(context, {
        type: 'doughnut',
        data: {
          labels: this.trickCategoryPoints.map((item) => item.label),
          datasets: [{
            data: this.trickCategoryPoints.map((item) => Number(item.validated_count) || Number(item.value) || 0),
            backgroundColor: ['#0f766e', '#0891b2', '#7c2d12', '#9333ea', '#be123c', '#4f46e5', '#65a30d'],
            borderWidth: 0,
          }],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' } },
          cutout: '58%',
        },
      });
    },
  },
};
</script>

<style scoped>
.competition-stats-backdrop {
  padding: 1rem;
  z-index: 1200;
}

.competition-stats-modal {
  width: min(1180px, calc(100vw - 2rem));
  max-width: 1180px;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  border-radius: 18px;
}

.competition-stats-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.competition-stats-heading h3 {
  margin: 0;
  font-size: 1.35rem;
  overflow-wrap: anywhere;
}

.competition-stats-eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
}

.competition-stats-subtitle {
  margin: 0.35rem 0 0;
  color: #6b7280;
}

.competition-stats-close {
  width: 2.5rem;
  height: 2.5rem;
  border: 0;
  border-radius: 999px;
  background: #eef2ff;
  color: #1f2937;
  flex-shrink: 0;
}

.competition-stats-body {
  overflow-y: auto;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.competition-stats-state {
  padding: 1rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  color: #475569;
}

.competition-stats-state-error {
  border-style: solid;
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.competition-stats-summary,
.competition-stats-charts,
.competition-selector-grid,
.competition-stage-grid {
  display: grid;
  gap: 1rem;
}

.competition-stats-summary {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.competition-summary-card,
.competition-chart-card,
.competition-stage-card,
.competition-selector-section,
.competition-stage-section,
.competition-round-panel {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
}

.competition-summary-card {
  padding: 0.95rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.competition-summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
}

.competition-summary-value {
  font-size: 1.35rem;
  color: #111827;
}

.competition-stats-charts {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.competition-chart-card {
  padding: 1rem;
  min-width: 0;
  overflow: hidden;
}

.competition-chart-card-secondary {
  align-self: start;
}

.competition-chart-card-wide {
  grid-column: 1 / -1;
}

.competition-chart-box {
  position: relative;
  min-height: 240px;
  width: 100%;
}

.competition-chart-box-short {
  min-height: 220px;
}

.competition-chart-box-horizontal {
  height: clamp(220px, 24vw, 280px);
}

.competition-chart-box-doughnut {
  height: clamp(240px, 24vw, 300px);
}

.competition-chart-box-line,
.competition-chart-box-bars {
  height: clamp(240px, 26vw, 310px);
}

.competition-chart-box canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
}

.competition-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.competition-section-header h4 {
  margin: 0;
  font-size: 1rem;
}

.competition-count-pill,
.competition-stage-pill,
.competition-stage-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.76rem;
  font-weight: 600;
}

.competition-count-pill,
.competition-stage-pill {
  background: #e5e7eb;
  color: #111827;
}

.competition-stage-section,
.competition-selector-section,
.competition-round-panel {
  padding: 1rem;
}

.competition-stage-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.competition-stage-card {
  padding: 0.95rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.competition-stage-card.is-selected {
  border-color: #1d4ed8;
  box-shadow: 0 10px 28px rgba(29, 78, 216, 0.12);
  transform: translateY(-1px);
}

.competition-stage-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}

.competition-stage-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  color: #4b5563;
  font-size: 0.86rem;
}

.competition-selector-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.competition-selector-field {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-size: 0.92rem;
  color: #475569;
}

.competition-selector-input {
  min-height: 44px;
}

.competition-stage-tag {
  background: #e0e7ff;
  color: #3730a3;
}

@media (max-width: 768px) {
  .competition-stats-modal {
    width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
  }

  .competition-stats-header,
  .competition-stats-body,
  .competition-chart-card,
  .competition-stage-section,
  .competition-selector-section,
  .competition-round-panel {
    padding-left: 0.9rem;
    padding-right: 0.9rem;
  }

  .competition-stats-header {
    align-items: flex-start;
  }

  .competition-stats-charts,
  .competition-selector-grid {
    grid-template-columns: 1fr;
  }

  .competition-chart-card-secondary {
    grid-column: 1 / -1;
  }

  .competition-chart-box {
    min-height: 220px;
    height: clamp(220px, 58vw, 300px);
  }

  .competition-chart-box-horizontal {
    min-height: 320px;
    height: clamp(320px, 88vw, 460px);
  }

  .competition-chart-box-doughnut {
    min-height: 240px;
    height: clamp(240px, 70vw, 320px);
  }

  .competition-chart-box-line,
  .competition-chart-box-bars {
    min-height: 240px;
    height: clamp(240px, 62vw, 320px);
  }

  .competition-section-header {
    align-items: flex-start;
  }

  .competition-section-header h4 {
    line-height: 1.3;
  }
}

@media (max-width: 480px) {
  .competition-stats-backdrop {
    padding: 0.4rem;
  }

  .competition-stats-modal {
    width: calc(100vw - 0.8rem);
    max-height: calc(100vh - 0.8rem);
  }

  .competition-chart-card {
    padding: 0.85rem;
  }

  .competition-chart-box-horizontal {
    min-height: 360px;
    height: clamp(360px, 108vw, 520px);
  }
}
</style>